import React, { useState, useEffect, useCallback } from 'react';
import { auth } from './firebase';
import { claimWing, initializeWingsData, getWingsClaimedToday, getLeaderboard, getTodayKey, ADMIN_EMAILS, hideFromLeaderboard, unhideFromLeaderboard } from '../utils/wingsUtility';
import './Wings.css';

const Wings = () => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [wingsData, setWingsData] = useState(null);
    const [leaderboard, setLeaderboard] = useState([]);
    const [claiming, setClaiming] = useState(false);
    const [showCongrats, setShowCongrats] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [showAuthModal, setShowAuthModal] = useState(false);
    const [showEmails, setShowEmails] = useState(false);

    // Auth state
    useEffect(() => {
        const unsubscribe = auth.onAuthStateChanged((currentUser) => {
            setUser(currentUser);
            setLoading(false);
        });
        return () => unsubscribe();
    }, []);

    // Load wings data and leaderboard
    const loadData = useCallback(async () => {
        if (user) {
            const data = await initializeWingsData(user.uid, user);
            setWingsData(data);
        }
        const adminUser = user?.email && ADMIN_EMAILS.includes(user.email);
        const lb = await getLeaderboard(!!adminUser);
        setLeaderboard(lb);
    }, [user]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    const handleClaim = async () => {
        if (!user) {
            setShowAuthModal(true);
            return;
        }

        setClaiming(true);
        setErrorMessage('');

        const result = await claimWing(user.uid, user);

        if (result.success) {
            // Update local state including dailyClaims so the today counter updates
            const todayKey = getTodayKey();
            setWingsData(prev => ({
                ...prev,
                totalWings: result.totalWings,
                dailyClaims: {
                    ...(prev?.dailyClaims || {}),
                    [todayKey]: result.claimedToday
                }
            }));
            setShowCongrats(true);
            // Refresh leaderboard
            const lb = await getLeaderboard(!!isAdmin);
            setLeaderboard(lb);
        } else {
            setErrorMessage(result.message);
        }

        setClaiming(false);
    };

    const claimedToday = wingsData ? getWingsClaimedToday(wingsData) : 0;
    const totalWings = wingsData?.totalWings || 0;
    const isAdmin = user?.email && ADMIN_EMAILS.includes(user.email);

    if (loading) {
        return (
             <div className="col-md-8 mx-auto text-center" style={{ paddingTop: '80px' }}>
                <div className="container text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                </div>
            </div>
        );
    }

    return (
    <div className="col-md-8 mx-auto text-center" style={{ paddingTop: '80px', paddingBottom: '60px' }}>
            <div className="container">
                {/* Header Section */}
                <div className="wings-header text-center">
                    <h1 className="wings-title">Claim Your Wings</h1>
                    <div className="wings-divider"></div>
                    <p className="wings-description">
                        Each time you put your PilotHelper peer support skills into action to help a fellow
                        aviation professional, you can claim your PilotHelper Wings. Check the leaderboard
                        to see who earned the most wings!!
                    </p>
                </div>

                {/* Wings Tracker */}
                {user && (
                    <div className="wings-tracker text-center">
                        <span className="wings-count-label">My Wings: </span>
                        <span className="wings-count-value">{totalWings}</span>
                        <span className="wings-daily-count">({claimedToday}/10 today)</span>
                    </div>
                )}

                {/* Wings Badge and Claim Button */}
                <div className="wings-claim-section text-center">
                    <div className="wings-badge-container">
                        <img
                            src="/assets/pilotWings.png"
                            alt="PilotHelper Peer Support Wings"
                            className="wings-badge-image"
                        />
                    </div>
                    <h3 className="wings-badge-title">Peer Support Wings</h3>
                    <button
                        className="wings-claim-btn"
                        onClick={handleClaim}
                        disabled={claiming}
                    >
                        {claiming ? (
                            <>
                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                Claiming...
                            </>
                        ) : (
                            'Claim'
                        )}
                    </button>

                    {errorMessage && (
                        <div className="wings-error-message" role="alert">
                            {errorMessage}
                        </div>
                    )}
                </div>

                {/* Leaderboard */}
                <div className="wings-leaderboard">
                    <h2 className="leaderboard-title text-center">LEADERBOARD</h2>
                    <div className={`leaderboard-table-container${isAdmin ? ' leaderboard-admin-scroll' : ''}`}>
                        <table className="leaderboard-table">
                            <thead>
                                <tr>
                                    <th className="lb-rank">RANK</th>
                                    <th className="lb-user">USER</th>
                                    {showEmails && <th className="lb-email">EMAIL</th>}
                                    <th className="lb-wings">WINGS</th>
                                    {isAdmin && <th className="lb-action">HIDE</th>}
                                </tr>
                            </thead>
                            <tbody>
                                {leaderboard.length > 0 ? (
                                    leaderboard.map((entry, index) => (
                                        <tr
                                            key={entry.id}
                                            className={`${index % 2 === 0 ? 'lb-row-even' : 'lb-row-odd'}${entry.hidden ? ' lb-row-hidden' : ''}`}
                                        >
                                            <td className="lb-rank">{index + 1}</td>
                                            <td className="lb-user" style={entry.hidden ? { textDecoration: 'line-through', opacity: 0.5 } : undefined}>
                                                {isAdmin ? entry.fullName : entry.name}
                                            </td>
                                            {showEmails && <td className="lb-email" style={entry.hidden ? { opacity: 0.5 } : undefined}>{entry.email}</td>}
                                            <td className="lb-wings" style={entry.hidden ? { opacity: 0.5 } : undefined}>{entry.wings.toLocaleString()}</td>
                                            {isAdmin && (
                                                <td className="lb-action">
                                                    <button
                                                        className={`btn btn-sm ${entry.hidden ? 'btn-outline-success' : 'btn-outline-danger'}`}
                                                        onClick={async () => {
                                                            try {
                                                                let result;
                                                                if (entry.hidden) {
                                                                    result = await unhideFromLeaderboard(entry.id);
                                                                } else {
                                                                    result = await hideFromLeaderboard(entry.id);
                                                                }
                                                                
                                                                if (result.success) {
                                                                    const lb = await getLeaderboard(true);
                                                                    setLeaderboard(lb);
                                                                } else {
                                                                    // Show error to user
                                                                    alert(result.message || 'Operation failed. You may not have permission.');
                                                                }
                                                            } catch (error) {
                                                                console.error('Error managing leaderboard visibility:', error);
                                                                alert('Failed to update leaderboard visibility. Please check your permissions.');
                                                            }
                                                        }}
                                                        title={entry.hidden ? 'Unhide from leaderboard' : 'Hide from leaderboard'}
                                                        aria-label={entry.hidden ? `Unhide ${entry.name} from leaderboard` : `Hide ${entry.name} from leaderboard`}
                                                    >
                                                        {entry.hidden ? '\u21A9' : '\u2716'}
                                                    </button>
                                                </td>
                                            )}
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan={isAdmin ? (showEmails ? 5 : 4) : (showEmails ? 4 : 3)} className="text-center py-4 text-muted">
                                            No wings claimed yet. Be the first!
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                {isAdmin && (
                    <div className="text-center mt-4">
                        <button
                            className="btn btn-sm btn-outline-secondary"
                            onClick={() => setShowEmails(prev => !prev)}
                        >
                            {showEmails ? 'Hide Emails' : 'Show Emails'}
                        </button>
                    </div>
                )}
            </div>

            {/* Congratulations Modal */}
            {showCongrats && (
                <div
                    className="modal show"
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="congrats-modal-title"
                    style={{ backgroundColor: 'rgba(0,0,0,0.5)', position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', zIndex: 1050, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                    onClick={() => setShowCongrats(false)}
                >
                    <div
                        className="modal-dialog"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="modal-content" style={{ borderRadius: '12px' }}>
                            <div className="modal-header border-0 pb-0 position-relative" style={{ justifyContent: 'center' }}>
                                <h5 id="congrats-modal-title" className="modal-title text-center" style={{ fontSize: '1.5rem', fontWeight: 600 }}>Congratulations 🎉</h5>
                                <button
                                    type="button"
                                    className="position-absolute"
                                    style={{ right: '1rem', top: '1rem', background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.5rem', color: '#333', padding: '0', lineHeight: 1 }}
                                    onClick={() => setShowCongrats(false)}
                                    aria-label="Close"
                                >&times;</button>
                            </div>
                            <div className="modal-body text-center" style={{ paddingTop: '12px' }}>
                                <p style={{ color: '#555', fontSize: '1.05rem', marginBottom: '20px' }}>
                                    You have successfully earned one PilotHelper Wing!
                                </p>
                                <button
                                    className="btn"
                                    style={{ backgroundColor: '#6c757d', color: 'white', borderRadius: '980px', padding: '5px 10px', fontSize: '1.1rem' }}
                                    onClick={() => setShowCongrats(false)}
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Auth Required Modal */}
            {showAuthModal && (
                <div
                    className="modal show"
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="auth-modal-title"
                    style={{ backgroundColor: 'rgba(0,0,0,0.5)', position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', zIndex: 1050, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                    onClick={() => setShowAuthModal(false)}
                >
                    <div
                        className="modal-dialog"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="modal-content">
                            <div className="modal-header border-0">
                                <h5 id="auth-modal-title" className="modal-title">Sign In Required</h5>
                                <button type="button" className="btn-close" onClick={() => setShowAuthModal(false)} aria-label="Close"></button>
                            </div>
                            <div className="modal-body text-center">
                                <p className="text-muted">
                                    Please sign in to claim your PilotHelper Wings.
                                </p>
                                <p className="text-muted small">
                                    Use the Sign In button in the navigation bar to get started.
                                </p>
                            </div>
                            <div className="modal-footer border-0 justify-content-center">
                                <button type="button" className="btn btn-secondary" onClick={() => setShowAuthModal(false)}>
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Wings;
